import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiRefreshCw, FiZap, FiDatabase, FiSettings } from 'react-icons/fi';
import * as api from '../api/api';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { getSessionPhase } from '../utils/sessionPhase';

const pct = (v) => v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%`;
const num = (v,d=1) => v == null ? '—' : Number(v).toFixed(d);
const setupName = (v) => (v || '—')
  .replace('_candidate_fit','')
  .replace('_fit','')
  .replaceAll('_',' ')
  .toUpperCase();

const Today = () => {
  const navigate = useNavigate();
  const localExecution = process.env.REACT_APP_LOCAL_EXECUTION === 'true';
  const [loading,setLoading] = useState(true);
  const [intel,setIntel] = useState(null);
  const [latest,setLatest] = useState(null);
  const [health,setHealth] = useState(null);
  const [bot,setBot] = useState(null);
  const [watchlist,setWatchlist] = useState([]);
  const [positions,setPositions] = useState([]);
  const [orders,setOrders] = useState([]);
  const [globalSettings,setGlobalSettings] = useState(null);
  const [actionableSort,setActionableSort] = useState('score');
  const [themeFilter,setThemeFilter] = useState('ALL');
  const [selectedSymbol,setSelectedSymbol] = useState(() => new URLSearchParams(window.location.search).get('ticker'));

  const refresh = async () => {
    try {
      const [s,l,h,b,w,g] = await Promise.all([
        api.getIntelligenceStatus(),
        api.getIntelligenceLatest(),
        api.getLocalHealth().catch(() => ({data: null})),
        api.getBotStatus().catch(() => ({data: null})),
        api.getWatchlist().catch(() => ({data: []})),
        api.getGlobalSettings().catch(() => ({data: null})),
      ]);
      setIntel(s.data);
      setLatest(l.data);
      setHealth(h.data);
      setBot(b.data);
      setWatchlist(w.data || []);
      setGlobalSettings(g.data || null);

      if (h.data?.broker_connected) {
        const [p,o] = await Promise.allSettled([api.getLivePositions(), api.getOpenOrders()]);
        setPositions(p.status === 'fulfilled' ? (p.value.data || []) : []);
        setOrders(o.status === 'fulfilled' ? (o.value.data || []) : []);
      } else {
        setPositions([]);
        setOrders([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load command center.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh,15000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedSymbol) url.searchParams.set('ticker', selectedSymbol);
    else url.searchParams.delete('ticker');
    window.history.replaceState({}, '', url);
  }, [selectedSymbol]);

  const runFull = () => toast.promise(api.runFullIntelligencePipeline(), {
    loading:'Refreshing full market picture…',
    success:()=>{ refresh(); return 'Market picture refreshed.'; },
    error:(e)=>e.response?.data?.detail || 'Scan failed.'
  });

  const daily = latest?.daily;
  const regime = daily?.regime;
  const leaders = daily?.scan?.leaders || [];
  const openingSnapshot = latest?.opening?.opening || null;
  const openingBlocked = Boolean(openingSnapshot?.blocked);
  const opening = openingSnapshot?.stocks_in_play || [];
  const themes = useMemo(
    () => Object.values(latest?.themes?.themes || {}).sort((a,b)=>Number(b.heat||0)-Number(a.heat||0)),
    [latest]
  );
  const leaderBySymbol = Object.fromEntries(leaders.map(row => [row.symbol,row]));

  const themeFor = (symbol) => themes.find(t => (t.tickers || []).includes(symbol));
  const primaryThemeLeaders = (theme) => (theme?.members || [])
    .filter(m => ['PRIMARY_LEADER','SECONDARY_LEADER'].includes(m.role))
    .slice(0,3)
    .map(m => m.symbol)
    .join(' · ') || '—';

  const actionableThemeNames = Array.from(new Set(
    opening
      .map(row => row.theme || themeFor(row.symbol)?.theme)
      .filter(Boolean)
  ));
  const filteredActionable = opening.filter(row => {
    if (themeFilter === 'ALL') return true;
    return (row.theme || themeFor(row.symbol)?.theme) === themeFilter;
  });
  const sortedActionable = [...filteredActionable].sort((a,b) => {
    if (actionableSort === 'score') return Number(b.algostock_score || 0) - Number(a.algostock_score || 0);
    if (actionableSort === 'fit') return Number(b.preferred_setup_score || 0) - Number(a.preferred_setup_score || 0);
    if (actionableSort === 'rvol') return Number(b.rvol5 || 0) - Number(a.rvol5 || 0);
    return 0;
  });
  const bestActionable = sortedActionable[0] || opening[0] || null;

  const whyTags = (row) => {
    const leader = leaderBySymbol[row.symbol] || {};
    const theme = themeFor(row.symbol);
    const explicit = Array.isArray(row.why_now) ? row.why_now.filter(Boolean) : [];
    if (explicit.length) return explicit.slice(0,3);
    const tags = [];
    const rvol = Number(row.rvol5 || 0);
    if (rvol >= 3) tags.push('Exceptional RVOL');
    else if (rvol >= 2) tags.push('Strong RVOL');
    else if (rvol >= 1.5) tags.push('RVOL confirmed');
    if (Number(row.algostock_score || leader.algostock_score || 0) >= 90) tags.push('AS90+');
    if (Number(leader.confirmed_catalyst_score ?? leader.fundamental_catalyst_score ?? 0) >= 3) tags.push('Catalyst');
    if (['HOT','ACTIVE'].includes(theme?.label)) tags.push(`${theme.label} theme`);
    if (Number(leader.distance_52w_high ?? -1) >= -0.03) tags.push('Near 52W high');
    return tags.slice(0,3);
  };

  const fitTone = (value) => {
    const score = Number(value || 0);
    if (score >= 80) return 'fit-top';
    if (score >= 70) return 'fit-strong';
    if (score >= 55) return 'fit-watch';
    return 'fit-low';
  };

  const selectedLeader = selectedSymbol ? (leaderBySymbol[selectedSymbol] || null) : null;
  const selectedOpening = selectedSymbol ? (opening.find(row => row.symbol === selectedSymbol) || null) : null;
  const selectedTheme = selectedSymbol ? (themeFor(selectedSymbol) || null) : null;
  const selectedIpo01 = selectedLeader?.setup_candidates?.IPO01 || null;
  const selectedPb01 = selectedLeader?.setup_candidates?.PB01 || null;
  const selectedPb02 = selectedLeader?.setup_candidates?.PB02 || null;
  const selectedAlgoDetail = selectedOpening?.algostock_score_detail || selectedLeader?.algostock_score_detail || null;
  const selectedFundamental = selectedLeader?.fundamental_profile || null;

  const synthetic = Boolean(intel?.synthetic_data || daily?.synthetic_data);
  const phase = getSessionPhase(intel?.calendar?.today_is_trading_session !== false);
  const openingSymbols = new Set(opening.map(row => row.symbol));
  const bench = leaders.filter(row => !openingSymbols.has(row.symbol)).slice(0,8);

  const executionEnabled = globalSettings?.execution_enabled !== false;
  const executionReady = Boolean(
    health?.runtime_alive &&
    health?.broker_connected &&
    !health?.live_unlocked &&
    executionEnabled &&
    !health?.last_error
  );
  const algoPositions = positions.filter(p => p.order_id);
  const externalPositions = positions.filter(p => !p.order_id);
  const algoOrders = orders.filter(o => String(o.order_ref || '').startsWith('ALGOSTOCK:'));
  const externalOrders = orders.filter(o => !String(o.order_ref || '').startsWith('ALGOSTOCK:'));

  const pipelineReady = {
    regime: Boolean(regime),
    themes: themes.length > 0,
    leaders: leaders.length > 0,
    opening: Boolean(latest?.opening && !openingBlocked),
    execution: executionReady,
  };

  if (loading) return <div className="workspace-page"><LoadingSpinner/></div>;

  const syntheticUiAllowed = process.env.REACT_APP_ALLOW_SYNTHETIC_UI === 'true';
  const realDataReady = Boolean(intel?.configured && (!intel?.synthetic_data || syntheticUiAllowed));

  if (!realDataReady) {
    return (
      <div className="workspace-page terminal-page">
        <div className="command-header">
          <div>
            <div className="eyebrow">Daily operating view</div>
            <h1 className="command-title">Today</h1>
            <div className="command-subtitle">Real market data is required before AlgoStock can build today’s market picture.</div>
          </div>
        </div>

        <div className="real-data-gate">
          <div className="real-data-icon"><FiDatabase/></div>
          <div className="real-data-copy">
            <span className="gate-kicker">MARKET DATA</span>
            <h2>{localExecution ? 'Market data not connected' : 'Market service unavailable'}</h2>
            <p>
              {localExecution
                ? `${intel?.synthetic_data ? 'Synthetic development data is active in the backend but hidden from the product UI. ' : ''}Connect the local market-data provider, then run the full scan to build Regime, Themes, Leaders and Stocks in Play.`
                : 'Market data is temporarily unavailable. Please try again shortly.'}
            </p>
            <div className="gate-actions">
              {localExecution && <button className="ws-button primary" onClick={()=>navigate('/workspace-settings')}><FiSettings/> Open Market Data Settings</button>}
              <button className={`ws-button ${localExecution ? '' : 'primary'}`} onClick={refresh}><FiRefreshCw/> Check again</button>
            </div>
          </div>
          <div className="gate-status">
            <div><span>Delivery</span><strong>{localExecution ? 'LOCAL' : 'ALGOSTOCK CLOUD'}</strong></div>
            <div><span>Market data</span><strong className="warn">UNAVAILABLE</strong></div>
            {localExecution && <>
              <div><span>Fake data</span><strong className="good">OFF</strong></div>
              <div><span>IBKR</span><strong className={health?.broker_connected ? 'good' : 'warn'}>{health?.broker_connected ? 'CONNECTED' : 'NOT CONNECTED'}</strong></div>
            </>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-page terminal-page">
      <div className="command-header">
        <div>
          <div className="eyebrow">Daily operating view</div>
          <h1 className="command-title">Today</h1>
          <div className="command-subtitle">Market context → themes → leaders → setups → plans.</div>
        </div>
        <div className="page-actions">
          <button className="ws-button" onClick={refresh}><FiRefreshCw/> Refresh</button>
          {localExecution && <button className="ws-button primary" onClick={runFull}><FiZap/> Refresh market picture</button>}
        </div>
      </div>

      <div className="workflow-strip">
        {[
          ['1','REGIME',pipelineReady.regime],
          ['2','THEMES',pipelineReady.themes],
          ['3','LEADERS',pipelineReady.leaders],
          ['4','OPENING',pipelineReady.opening],
          ['5','EXECUTION',pipelineReady.execution],
        ].filter(([,label]) => localExecution || label !== 'EXECUTION').map(([n,label,ok])=>(
          <div className={`workflow-step ${ok ? 'done' : ''}`} key={label}>
            <span>{n}</span><strong>{label}</strong><em>{ok ? 'READY' : 'PENDING'}</em>
          </div>
        ))}
      </div>

      <div className="session-action-bar">
        <span className={'session-phase phase-' + phase.key.toLowerCase()}>{phase.label}</span>
        <strong>NEXT ACTION</strong>
        <span>{openingBlocked ? `BLOCKED: ${openingSnapshot?.block_reason || 'Market data is incomplete.'}` : phase.action}</span>
        {synthetic && <em>DEMO DATA · NO TRADING</em>}
      </div>

      <div className="decision-context">
        <section className="decision-card regime-card">
          <div className="decision-label">MARKET CONTEXT</div>
          <div className="decision-regime">
            <strong>{String(regime?.label || 'NO REGIME').replaceAll('_',' ')}</strong>
            <span>{num(regime?.score,0)} / 100 research</span>
          </div>
          <div className="decision-sub">{regime?.risk_posture || 'No risk posture'} · {regime?.research_risk_multiplier ?? '—'}R research size · {regime?.coverage?.status || 'UNKNOWN'} coverage</div>
          {(regime?.explanation?.supporting_evidence?.length || regime?.explanation?.contrary_evidence?.length) ? (
            <div className="decision-evidence">
              {regime?.explanation?.supporting_evidence?.slice(0,2).map(item=><span className="evidence-support" key={item}>+ {item}</span>)}
              {regime?.explanation?.contrary_evidence?.slice(0,1).map(item=><span className="evidence-contrary" key={item}>− {item}</span>)}
            </div>
          ) : null}
        </section>

        <section className="decision-card">
          <div className="decision-label">TOP THEME</div>
          <div className="decision-main">{themes[0]?.theme || '—'}</div>
          <div className="decision-sub">{themes[0]?.label || '—'} · Heat {num(themes[0]?.heat,0)} · {themes[0]?.acceleration?.state || '—'}</div>
        </section>

        <section className="decision-card">
          <div className="decision-label">LEADERS</div>
          <div className="decision-main">{leaders.length}</div>
          <div className="decision-sub">Hard-gated daily shortlist</div>
        </section>

        <section className="decision-card">
          <div className="decision-label">STOCKS IN PLAY</div>
          <div className="decision-main">{openingBlocked ? 'BLOCKED' : opening.length}</div>
          <div className="decision-sub">{openingBlocked ? `Missing: ${(openingSnapshot?.unknown_inputs || []).join(', ') || 'critical market inputs'}` : 'Opening candidates · plan required before any trade'}</div>
        </section>

        <section className="decision-card">
          <div className="decision-label">TOP CANDIDATE</div>
          <div className="decision-main">{bestActionable?.symbol || '—'}</div>
          <div className="decision-sub">{openingBlocked ? 'Opening ranking withheld until market coverage is complete.' : (bestActionable ? `AlgoStock ${num(bestActionable.algostock_score,1)} · ${bestActionable.daily_setup_family || setupName(bestActionable.preferred_setup)} · ${num(bestActionable.rvol5,2)}x RVOL · Plan required` : 'No opening candidate')}</div>
        </section>

        {localExecution && (
          <section className="decision-card">
            <div className="decision-label">EXECUTION</div>
            <div className={`decision-main ${executionReady ? 'positive' : ''}`}>{executionReady ? 'READY' : 'NOT READY'}</div>
            <div className="decision-sub">{bot?.is_running ? 'Engine running' : 'Engine stopped'} · Paper locked</div>
          </section>
        )}
      </div>

      <div className="decision-stack">
        <section className="terminal-panel decision-wide">
          <div className="terminal-panel-head actionable-head">
            <div>
              <span>STOCKS IN PLAY</span>
              <em>{filteredActionable.length} shown · ranked by AlgoStock Score · plan still required</em>
            </div>
            <div className="actionable-controls">
              <select value={themeFilter} onChange={e=>setThemeFilter(e.target.value)} aria-label="Filter stocks in play by theme">
                <option value="ALL">All themes</option>
                {actionableThemeNames.map(theme => <option value={theme} key={theme}>{theme}</option>)}
              </select>
              <div className="sort-tabs">
                <button className={actionableSort==='score' ? 'active':''} onClick={()=>setActionableSort('score')}>AlgoStock Score</button>
                <button className={actionableSort==='fit' ? 'active':''} onClick={()=>setActionableSort('fit')}>Opening heuristic</button>
                <button className={actionableSort==='rvol' ? 'active':''} onClick={()=>setActionableSort('rvol')}>RVOL</button>
              </div>
            </div>
          </div>
          {opening.length ? (
            <div className="ws-table-wrap">
              <table className="terminal-table actionable-table actionable-matrix">
                <thead>
                  <tr>
                    <th>#</th><th>Ticker</th><th>Theme</th><th>Why now</th><th>AlgoStock</th>
                    <th>RVOL</th><th>Gap</th><th>Daily setup</th><th>Opening setup</th><th>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedActionable.slice(0,12).map((r,i) => {
                    return (
                      <tr key={r.symbol} className="clickable-row" onClick={()=>setSelectedSymbol(r.symbol)}>
                        <td>{i+1}</td>
                        <td><strong className="ticker actionable-ticker">{r.symbol}</strong></td>
                        <td className="theme-cell">{r.theme || themeFor(r.symbol)?.theme || '—'}</td>
                        <td className="why-cell"><div className="why-tags">{whyTags(r).map(tag=><span key={tag}>{tag}</span>)}</div></td>
                        <td><strong>{num(r.algostock_score,1)}</strong></td>
                        <td className={Number(r.rvol5)>=2 ? 'positive':''}>{num(r.rvol5,2)}x</td>
                        <td>{pct(r.gap_pct)}</td>
                        <td><strong className="preferred-setup">{r.daily_setup_family || '—'} {r.daily_setup_family && <small>{r.daily_setup_research_status || '—'}</small>}</strong></td>
                        <td><strong className="preferred-setup">{setupName(r.preferred_setup)} <small>{num(r.preferred_setup_score,0)}</small></strong></td>
                        <td><span className="decision-state-pill">{r.decision_state || 'PLAN_REQUIRED'}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <div className="terminal-empty">No stock has cleared the opening data/RVOL candidate gate.</div>}
        </section>

        <section className="terminal-panel decision-wide">
          <div className="terminal-panel-head">
            <span>BENCH / DAILY LEADERS</span>
            <em>Valid leaders waiting for opening confirmation</em>
          </div>
          {bench.length ? (
            <div className="ws-table-wrap">
              <table className="terminal-table bench-table">
                <thead>
                  <tr>
                    <th>#</th><th>Ticker</th><th>Theme</th><th>AlgoStock Score</th><th>Catalyst</th>
                    <th>1M</th><th>3M</th><th>6M</th><th>ATR%</th><th>From 52W High</th>
                  </tr>
                </thead>
                <tbody>
                  {bench.map((r,i)=>(
                    <tr key={r.symbol} className="clickable-row" onClick={()=>setSelectedSymbol(r.symbol)}>
                      <td>{i+1}</td>
                      <td><strong className="ticker">{r.symbol}</strong></td>
                      <td>{themeFor(r.symbol)?.theme || '—'}</td>
                      <td><strong>{num(r.algostock_score,1)}</strong></td>
                      <td>{(r.confirmed_catalyst_score ?? r.fundamental_catalyst_score) == null ? '—' : `${num(r.confirmed_catalyst_score ?? r.fundamental_catalyst_score,1)}/5`}</td>
                      <td>{num(r.momentum_percentile_1m,0)}</td>
                      <td>{num(r.momentum_percentile_3m,0)}</td>
                      <td>{num(r.momentum_percentile_6m,0)}</td>
                      <td>{pct(r.atr_pct)}</td>
                      <td>{pct(r.distance_52w_high)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="terminal-empty">No additional daily leaders waiting on confirmation.</div>}
        </section>

        <div className="decision-two">
          <section className="terminal-panel">
            <div className="terminal-panel-head"><span>THEMES</span><em>Confirmed narrative + quant</em></div>
            <table className="terminal-table theme-decision-table">
              <thead><tr><th>Theme</th><th>Status</th><th>Heat</th><th>Catalyst</th><th>Accel.</th><th>Leaders</th></tr></thead>
              <tbody>
                {themes.slice(0,6).map(t=>(
                  <tr key={t.theme}>
                    <td><strong>{t.theme}</strong></td>
                    <td>{t.label || '—'}</td>
                    <td>{num(t.heat,0)}</td>
                    <td>{t.narrative?.catalyst_quality == null ? '—' : num(t.narrative.catalyst_quality,0)}</td>
                    <td>{t.acceleration?.state || '—'}</td>
                    <td>{primaryThemeLeaders(t)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {localExecution && <section className="terminal-panel">
            <div className="terminal-panel-head"><span>EXECUTION READINESS</span><em>Awareness only</em></div>
            <div className="execution-summary">
              <div><span>Runtime</span><strong className={health?.runtime_alive?'good':'bad'}>{health?.runtime_alive?'ONLINE':'DOWN'}</strong></div>
              <div><span>IBKR</span><strong className={health?.broker_connected?'good':'warn'}>{health?.broker_connected?'CONNECTED':'NOT CONNECTED'}</strong></div>
              <div><span>Algo positions</span><strong>{algoPositions.length}</strong></div>
              <div><span>External positions</span><strong className={externalPositions.length?'warn':''}>{externalPositions.length}</strong></div>
              <div><span>Algo orders</span><strong>{algoOrders.length}</strong></div>
              <div><span>External orders</span><strong className={externalOrders.length?'warn':''}>{externalOrders.length}</strong></div>
              <div><span>Watchlist</span><strong>{watchlist.length}</strong></div>
            </div>
            <button className="terminal-link" onClick={()=>navigate('/control-deck')}>Open Execution <FiArrowRight/></button>
          </section>}
        </div>

        <div className="decision-footer-links">
          <button className="terminal-link" onClick={()=>navigate('/market-intelligence')}>Full Intelligence <FiArrowRight/></button>
          {localExecution && <button className="terminal-link" onClick={()=>navigate('/analytics')}>Trade Research <FiArrowRight/></button>}
          {localExecution && <button className="terminal-link" onClick={()=>navigate('/research-validation')}>Filter Validation <FiArrowRight/></button>}
        </div>
      </div>

      {selectedSymbol && (
        <div className="inspector-backdrop" onClick={()=>setSelectedSymbol(null)}>
          <aside className="ticker-inspector" onClick={e=>e.stopPropagation()}>
            <div className="inspector-head">
              <div>
                <div className="eyebrow">Ticker context</div>
                <div className="inspector-symbol">{selectedSymbol}</div>
                <div className="inspector-company">{selectedLeader?.company_name || '—'}</div>
              </div>
              <button className="inspector-close" onClick={()=>setSelectedSymbol(null)}>×</button>
            </div>

            <div className="inspector-meta">
              <span>{selectedLeader?.sector || '—'}</span>
              <span>{selectedLeader?.industry || '—'}</span>
              <span>{selectedLeader?.exchange || '—'}</span>
            </div>

            <div className="inspector-section">
              <div className="inspector-section-title">AlgoStock Score</div>
              <div className="inspector-kpis">
                <div><span>AlgoStock Score</span><strong>{num(selectedOpening?.algostock_score ?? selectedLeader?.algostock_score,1)}</strong></div>
                <div><span>Tier</span><strong>{selectedAlgoDetail?.tier || '—'}</strong></div>
                <div><span>Leader quality</span><strong>{num(selectedAlgoDetail?.components?.leader_quality,0)}</strong></div>
                <div><span>Group strength</span><strong>{num(selectedAlgoDetail?.components?.group_strength,0)}</strong></div>
                <div><span>Setup quality</span><strong>{num(selectedAlgoDetail?.components?.setup_quality,0)}</strong></div>
                <div><span>Market regime</span><strong>{num(selectedAlgoDetail?.components?.market_regime,0)}</strong></div>
                <div><span>Fundamental</span><strong>{num(selectedAlgoDetail?.components?.fundamental_quality,0)}</strong></div>
                <div><span>Catalyst</span><strong>{num(selectedAlgoDetail?.components?.confirmed_catalyst,0)}</strong></div>
                {selectedAlgoDetail?.opening_confirmation != null && <div><span>Opening</span><strong>{num(selectedAlgoDetail.opening_confirmation,0)}</strong></div>}
                <div><span>Evidence</span><strong>{selectedAlgoDetail?.evidence_state || '—'}</strong></div>
                <div><span>Alignment</span><strong>{num(selectedAlgoDetail?.evidence_alignment,0)}</strong></div>
                <div><span>Coverage</span><strong>{selectedAlgoDetail?.coverage == null ? '—' : Math.round(Number(selectedAlgoDetail.coverage) * 100) + '%'}</strong></div>
              </div>
            </div>

            {selectedFundamental && (
              <div className="inspector-section">
                <div className="inspector-section-title">Fundamental / Expectations Momentum</div>
                <div className="inspector-kpis">
                  <div><span>Fund. score</span><strong>{num(selectedFundamental?.score,0)}</strong></div>
                  <div><span>Fund. coverage</span><strong>{selectedFundamental?.coverage == null ? '—' : Math.round(Number(selectedFundamental.coverage) * 100) + '%'}</strong></div>
                  <div><span>Revenue YoY</span><strong>{pct(selectedFundamental?.statements?.revenue_yoy)}</strong></div>
                  <div><span>EPS YoY</span><strong>{selectedFundamental?.statements?.eps_turnaround ? 'TURNAROUND' : pct(selectedFundamental?.statements?.eps_yoy)}</strong></div>
                  <div><span>Revenue accel.</span><strong>{pct(selectedFundamental?.statements?.revenue_acceleration)}</strong></div>
                  <div><span>EPS accel.</span><strong>{pct(selectedFundamental?.statements?.eps_acceleration)}</strong></div>
                  <div><span>EPS surprise</span><strong>{pct(selectedFundamental?.earnings?.eps_surprise_pct)}</strong></div>
                  <div><span>Revenue surprise</span><strong>{pct(selectedFundamental?.earnings?.revenue_surprise_pct)}</strong></div>
                  <div><span>EPS revision</span><strong>{pct(selectedFundamental?.revisions?.eps_revision)}</strong></div>
                  <div><span>Revenue revision</span><strong>{pct(selectedFundamental?.revisions?.revenue_revision)}</strong></div>
                  <div><span>Float shares</span><strong>{selectedFundamental?.float?.float_shares == null ? '—' : Number(selectedFundamental.float.float_shares).toLocaleString()}</strong></div>
                  <div><span>Source</span><strong>{selectedFundamental?.source || '—'}</strong></div>
                </div>
              </div>
            )}

            <div className="inspector-section">
              <div className="inspector-section-title">Momentum Rank</div>
              <div className="rank-bars">
                {[
                  ['1M',selectedLeader?.momentum_percentile_1m],
                  ['3M',selectedLeader?.momentum_percentile_3m],
                  ['6M',selectedLeader?.momentum_percentile_6m],
                ].map(([label,value])=>(
                  <div className="rank-bar" key={label}>
                    <span>{label}</span>
                    <div><i style={{width:`${Math.max(0,Math.min(100,Number(value)||0))}%`}}/></div>
                    <strong>{num(value,0)}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="inspector-section">
              <div className="inspector-section-title">Trend / Extension</div>
              <div className="inspector-kpis">
                <div><span>10EMA ext.</span><strong>{pct(selectedLeader?.extension_10ema)}</strong></div>
                <div><span>20EMA ext.</span><strong>{pct(selectedLeader?.extension_20ema)}</strong></div>
                <div><span>RS SPY 1M</span><strong>{pct(selectedLeader?.rs_spy_1m)}</strong></div>
                <div><span>RS SPY 3M</span><strong>{pct(selectedLeader?.rs_spy_3m)}</strong></div>
                <div><span>Residual 3M rank</span><strong>{num(selectedLeader?.market_residual_momentum_3m_pctile,0)}</strong></div>
                <div><span>Group participation</span><strong>{num(selectedLeader?.industry_participation_percentile ?? selectedLeader?.sector_participation_percentile,0)}</strong></div>
                <div><span>Dollar volume 20d</span><strong>{selectedLeader?.avg_dollar_volume20 == null ? '—' : '$' + Math.round(Number(selectedLeader.avg_dollar_volume20) / 1000000) + 'M'}</strong></div>
                <div><span>ATR %</span><strong>{pct(selectedLeader?.atr_pct)}</strong></div>
              </div>
            </div>

            <div className="inspector-section">
              <div className="inspector-section-title">Why this leader?</div>
              <div className="evidence-list">
                {(selectedLeader?.explanation?.supporting_evidence || []).map(item=><span className="evidence-support" key={item}>+ {item}</span>)}
                {(selectedLeader?.explanation?.contrary_evidence || []).map(item=><span className="evidence-contrary" key={item}>− {item}</span>)}
                {!selectedLeader?.explanation && <span className="inspector-empty">No explanation snapshot available.</span>}
              </div>
            </div>

            <div className="inspector-section">
              <div className="inspector-section-title">Theme</div>
              {selectedTheme ? (
                <div className="theme-inspector">
                  <strong>{selectedTheme.theme}</strong>
                  <span>{selectedTheme.label} · Heat {num(selectedTheme.heat,0)} · {selectedTheme.acceleration?.state || '—'}</span>
                  <p>{selectedTheme.subtheme || selectedTheme.narrative?.notes || '—'}</p>
                </div>
              ) : <div className="inspector-empty">No confirmed theme.</div>}
            </div>

            <div className="inspector-section">
              <div className="inspector-section-title">Research Setup Structure</div>
              {selectedIpo01 && (
                <>
                  <div className="inspector-kpis">
                    <div><span>IPO01 state</span><strong>{selectedIpo01.state || '—'}</strong></div>
                    <div><span>Listed sessions</span><strong>{selectedIpo01.structure?.listing_sessions ?? '—'}</strong></div>
                    <div><span>Base depth</span><strong>{selectedIpo01.structure?.base_depth == null ? '—' : pct(selectedIpo01.structure.base_depth)}</strong></div>
                    <div><span>Setup quality</span><strong>{num(selectedIpo01.setup_quality_score,0)}</strong></div>
                    <div><span>Trigger ref.</span><strong>{num(selectedIpo01.structure?.trigger_reference,2)}</strong></div>
                    <div><span>Structural low</span><strong>{num(selectedIpo01.structure?.structural_low_reference,2)}</strong></div>
                  </div>
                  <p className="inspector-note">
                    {selectedIpo01.state === 'STRUCTURE_CANDIDATE'
                      ? 'Recent-IPO base candidate. Short listing history is evaluated with the IPO model instead of requiring a 200-day trend template.'
                      : 'Recent-IPO structure is being tracked, but the IPO01 hypothesis gates are not currently satisfied.'}
                  </p>
                </>
              )}
              {selectedPb01 ? (
                <>
                  <div className="inspector-kpis">
                    <div><span>PB01 state</span><strong>{selectedPb01.state || '—'}</strong></div>
                    <div><span>Research</span><strong>{selectedPb01.research_status || '—'}</strong></div>
                    <div><span>5d / 20d range</span><strong>{num(selectedPb01.structure?.range_5v20,2)}</strong></div>
                    <div><span>5d / 20d volume</span><strong>{num(selectedPb01.structure?.volume_5v20,2)}</strong></div>
                    <div><span>Resistance ref.</span><strong>{num(selectedPb01.structure?.full_base_resistance,2)}</strong></div>
                    <div><span>Trigger ref.</span><strong>{num(selectedPb01.structure?.trigger_reference,2)}</strong></div>
                    <div><span>Structural low ref.</span><strong>{num(selectedPb01.structure?.structural_low_reference,2)}</strong></div>
                    <div><span>VCP range</span><strong>{selectedPb01.vcp_evidence?.progressive_range_contraction ? 'YES' : 'NO'}</strong></div>
                    <div><span>VCP volume</span><strong>{selectedPb01.vcp_evidence?.progressive_volume_contraction ? 'YES' : 'NO'}</strong></div>
                    <div><span>Context tag</span><strong>{(selectedPb01.context_tags || []).join(', ') || '—'}</strong></div>
                  </div>
                  <p className="inspector-note">
                    {selectedPb01.state === 'STRUCTURE_CANDIDATE'
                      ? 'PB01 structure candidate only. Trigger, max entry, stop, event context and expiry must still be frozen in a Trade Plan.'
                      : 'PB01 hypothesis gates are not currently satisfied. This is not a trade-ready signal.'}
                  </p>
                </>
              ) : <div className="inspector-empty">No PB01 research structure available for this leader.</div>}
              {selectedPb02 && (
                <>
                  <div className="inspector-kpis" style={{marginTop:'10px'}}>
                    <div><span>PB02 state</span><strong>{selectedPb02.state || '—'}</strong></div>
                    <div><span>Research</span><strong>{selectedPb02.research_status || '—'}</strong></div>
                    <div><span>Pullback sessions</span><strong>{selectedPb02.structure?.pullback_sessions ?? '—'}</strong></div>
                    <div><span>Trigger ref.</span><strong>{num(selectedPb02.structure?.trigger_reference,2)}</strong></div>
                    <div><span>EMA10 ref.</span><strong>{num(selectedPb02.structure?.ema10_reference,2)}</strong></div>
                    <div><span>Structural low ref.</span><strong>{num(selectedPb02.structure?.structural_low_reference,2)}</strong></div>
                  </div>
                  <p className="inspector-note">
                    {selectedPb02.state === 'STRUCTURE_CANDIDATE'
                      ? 'PB02 pullback candidate only. Recovery trigger, max entry, stop, event context and expiry must still be frozen in a Trade Plan.'
                      : 'PB02 hypothesis gates are not currently satisfied. This is not a trade-ready signal.'}
                  </p>
                </>
              )}
            </div>

            <div className="inspector-section">
              <div className="inspector-section-title">Opening Candidate Context</div>
              {selectedOpening ? (
                <>
                  <div className="inspector-kpis">
                    <div><span>RVOL5</span><strong>{num(selectedOpening.rvol5,2)}x</strong></div>
                    <div><span>Gap</span><strong>{pct(selectedOpening.gap_pct)}</strong></div>
                    <div><span>OR/ATR</span><strong>{num(selectedOpening.opening_range_atr,2)}</strong></div>
                    <div><span>Heuristic fit</span><strong>{setupName(selectedOpening.preferred_setup)}</strong></div>
                  </div>
                  <p className="inspector-note">{selectedOpening.wait_for || 'Freeze a Trade Plan before execution.'}</p>
                  <div className="fit-grid">
                    {[
                      ['ORB',selectedOpening.strategy_fit?.orb_fit],
                      ['EP',selectedOpening.strategy_fit?.episodic_pivot_fit],
                      ['VWAP',selectedOpening.strategy_fit?.vwap_reclaim_candidate_fit],
                      ['R2G',selectedOpening.strategy_fit?.red_to_green_fit],
                    ].map(([label,value])=>(
                      <div key={label} className={'fit-box ' + fitTone(value)}>
                        <span>{label}</span><strong>{num(value,0)}</strong>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div className="inspector-empty">Waiting for opening confirmation.</div>}
            </div>

            <div className="inspector-section plan-inspector-action">
              <div className="inspector-section-title">Plan</div>
              <p>Freeze your trigger, invalidation, stop and risk before execution.</p>
              <button className="ws-button primary" onClick={()=>{
                const setup = selectedIpo01?.state === 'STRUCTURE_CANDIDATE'
                  ? 'IPO01'
                  : selectedPb01?.state === 'STRUCTURE_CANDIDATE'
                    ? 'PB01'
                    : selectedPb02?.state === 'STRUCTURE_CANDIDATE' ? 'PB02' : '';
                navigate('/trade-plans?ticker=' + encodeURIComponent(selectedSymbol) + (setup ? '&setup=' + setup : ''));
              }}>
                Open Trade Plan <FiArrowRight/>
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default Today;
